import { useCallback, useEffect, useRef, useState } from "react";
import useApi from "../../hooks/useApi";
import useAccessScope from "../../hooks/useAccessScope";
import useInterruptibleMutation from "../../hooks/useInterruptibleMutation";
import SecrRender from "./SecrRender";
import { InlineAlert, LoadingSpinner } from "../BaseComponents";
import { formatDate, getApiErrorMessage, getCSRFToken } from "../../lib/utils";


type SecrViewProps = {
    reportId: string;
}

function SecrDisplay({ reportId }: SecrViewProps) {
    const [data, setData] = useState<any>();
    const [baselineData, setBaselineData] = useState<any>();
    const [loading, setLoading] = useState(true);
    const [companyReports, setCompanyReports] = useState<any[]>([]);
    // #884: the now-rejecting READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE notify-only codes
    // (#857 item 6) previously had no catch here, so a rejection left `loading` stuck true
    // forever with no visible feedback instead of showing a failure.
    const [loadError, setLoadError] = useState('');
    const [publishError, setPublishError] = useState('');

    const api = useApi();
    const { generation, isCurrent } = useAccessScope(api, reportId);
    const [loadedGeneration, setLoadedGeneration] = useState<number>();
    const loadController = useRef<AbortController>();
    const publishingGeneration = useRef<number>();
    const startPublish = useInterruptibleMutation(api, reportId,
        'Publishing was interrupted and may already have completed. Reopen the report and check its status before trying again.');

    const loadData = useCallback(async () => {
        loadController.current?.abort();
        const controller = new AbortController();
        loadController.current = controller;
        setLoading(true);
        setLoadedGeneration(generation);
        setLoadError('');
        // Also clear a stale publish-failure banner from a previous report: `reportId` changing
        // re-runs this via the effect above, and a leftover `publishError` would otherwise keep
        // showing "Failed to publish this report" against an unrelated, newly-loaded report.
        setPublishError('');
        try {
            const [result, baselineResult] = await Promise.all([
                api.get(`secr/${reportId}`, { signal: controller.signal }),
                api.get(`secr/${reportId}/baseline`, { signal: controller.signal }),
            ]);

            if (controller.signal.aborted || !isCurrent(generation)) return;
            setData(result.data.report);
            setCompanyReports(result.data.companyReports);
            setBaselineData(baselineResult.data);
        } catch (err) {
            if (!controller.signal.aborted && isCurrent(generation)) {
                setLoadError(getApiErrorMessage(err, 'Failed to load this report. Please try again.'));
            }
        } finally {
            if (!controller.signal.aborted && isCurrent(generation)) setLoading(false);
        }
    }, [api, reportId, generation, isCurrent])

    useEffect(() => {
        loadData();
        return () => loadController.current?.abort();
    }, [loadData])

    const onPublish = async () => {
        if (loading || loadedGeneration !== generation || !isCurrent(generation) || publishingGeneration.current === generation) return;
        publishingGeneration.current = generation;
        const { signal, onSubmitted, finish } = startPublish();
        setLoading(true);
        setPublishError('');
        try {
            const csrf = await getCSRFToken(signal);
            if (signal.aborted || !isCurrent(generation)) return;
            onSubmitted();
            await api.post(`secr/${reportId}/publish/`, {
                _csrf: csrf
            }, { signal });
            if (signal.aborted || !isCurrent(generation)) return;
            finish();
            await loadData();
        } catch (err) {
            if (!signal.aborted && isCurrent(generation)) {
                setPublishError(getApiErrorMessage(err, 'Failed to publish this report. Please try again.'));
                setLoading(false);
            }
        } finally {
            finish();
            if (publishingGeneration.current === generation) publishingGeneration.current = undefined;
        }
    }

    if(loading || loadedGeneration !== generation) {
        return (
            <LoadingSpinner />
        )
    }

    if(loadError) {
        return (
            <InlineAlert type='error' data-testid='secr-display-load-error'>{loadError}</InlineAlert>
        )
    }

    return (
        <>
            { publishError && (
                <InlineAlert type='error' className='mb-4' data-testid='secr-display-publish-error'>{publishError}</InlineAlert>
            )}
            <SecrRender
                isOwn
                report={data}
                baselineReport={baselineData}
                onPublish={onPublish}
                companyReports={companyReports.map(report => {
                    return {
                        id: report.id,
                        label: formatDate(report.start_date) + ' - ' + formatDate(report.end_date)
                    }
                })}
            />
        </>
    );
}
export default SecrDisplay;
